import { useEffect, useRef, useState } from 'react';
import { X, Search, Send, User, Loader2, AlertTriangle } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../Toast';
import { SeletorDestinatariosCliente, enviarParaSelecionados, resumoFalhas, motivoLegivel, type FalhasEnvio } from '../whatsapp/ModalEnviarWhatsAppCliente';

interface ContatoSugestao {
  id: number;
  nome: string;
  push_name: string | null;
  telefone: string | null;
  jid: string;
  foto_url: string | null;
}

interface Props {
  // 'nfce' ou 'nfe' — define o endpoint /api/{tipo}/{id}/enviar-whatsapp
  tipo: 'nfce' | 'nfe';
  id: number;
  numero: number;
  // Total exibido pra contexto (pra eventualmente compor mensagem)
  // Atualmente nao usado na UI, backend monta a mensagem padrao se nao vier custom
  // Aviso amarelo no topo (ex: "Cliente nao possui WhatsApp cadastrado.")
  aviso?: string;
  // Pre-seleciona contato cujo telefone bate com esse digits (cliente do orcamento)
  preSelecionarTelefone?: string | null;
  // Quando vem, o modal abre com os contatos da ficha do cliente ja marcados (peca unica de
  // destinatarios). Sem ele, mantem o modo de 1 contato (ex.: troca/orcamento/Consumidor).
  clienteId?: number | null;
  // Nome do cliente exibido no contexto do modo multi.
  clienteNome?: string;
  onClose: (enviado?: boolean) => void;
}

// Dispatcher: com clienteId -> envio em massa pros contatos do cliente; sem ele -> 1 contato.
export function ModalEnviarWhatsAppNF(props: Props) {
  if (props.clienteId) return <ModalEnviarNFMultiCliente {...props} clienteId={props.clienteId} />;
  return <ModalEnviarNFSingle {...props} />;
}

// --- Modo multi: NF pros contatos da ficha do cliente (todos ja marcados) ---
function ModalEnviarNFMultiCliente({ tipo, id, numero, aviso, clienteId, clienteNome, onClose }: Props & { clienteId: number }) {
  const toast = useToast();
  const [mensagem, setMensagem] = useState('');
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [enviando, setEnviando] = useState(false);
  const [falhas, setFalhas] = useState<FalhasEnvio>(new Map());
  const fileName = `${tipo === 'nfe' ? 'NFe' : 'NFCe'}-${String(numero).padStart(9, '0')}.pdf`;
  const total = selecionados.size;

  async function handleEnviar() {
    if (!total) return;
    setEnviando(true);
    setFalhas(new Map());
    const { ok, falhas: f } = await enviarParaSelecionados([...selecionados], async (digits) => {
      await api.post(`/api/${tipo}/${id}/enviar-whatsapp`, {
        jid: digits,
        ...(mensagem.trim() ? { mensagem: mensagem.trim() } : {}),
      });
    });
    setEnviando(false);
    setFalhas(f);
    if (ok > 0 && f.size === 0) {
      toast.sucesso(ok === 1 ? 'Documento enviado pelo WhatsApp' : `Documento enviado para ${ok} contatos`);
      setTimeout(() => onClose(true), 600);
    } else if (ok > 0) {
      toast.aviso(`Enviado para ${ok} de ${ok + f.size}. ${resumoFalhas(f)}`);
    } else {
      toast.erro(resumoFalhas(f));
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[80]">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-800">Enviar documento pelo WhatsApp</h3>
          <button onClick={() => onClose(false)} className="p-1 hover:bg-gray-100 rounded-lg transition" aria-label="Fechar">
            <X size={20} className="text-gray-400" />
          </button>
        </div>
        <div className="p-5">
          {aviso && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 mb-3 text-sm text-amber-700">
              <AlertTriangle size={16} className="shrink-0" />
              <span>{aviso}</span>
            </div>
          )}
          {clienteNome && <div className="text-sm text-gray-600 mb-3">Para: <strong>{clienteNome}</strong></div>}
          <div className="bg-gray-50 rounded-lg px-3 py-2 mb-3 text-sm text-gray-600 flex items-center gap-2">
            <span className="text-blue-500 font-medium">PDF</span>
            <span className="truncate">{fileName}</span>
          </div>
          <textarea
            value={mensagem}
            onChange={(e) => setMensagem(e.target.value)}
            placeholder="Mensagem (opcional — se vazio, vai a padrao com total e agradecimento)"
            rows={3}
            className="no-upper w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none resize-none mb-3"
          />
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Contatos do cliente</div>
          <SeletorDestinatariosCliente
            clienteId={clienteId}
            selecionados={selecionados}
            onChange={setSelecionados}
            falhas={falhas}
            desabilitado={enviando}
          />
        </div>
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-200">
          <button onClick={() => onClose(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition">Cancelar</button>
          <button
            onClick={() => void handleEnviar()}
            disabled={total === 0 || enviando}
            className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition ${
              total > 0 && !enviando ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {enviando ? <><Loader2 size={16} className="animate-spin" />Enviando...</> : <><Send size={16} />Enviar{total > 0 ? ` (${total})` : ''}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatarTelefoneBr(phone: string | null): string {
  if (!phone) return '';
  const clean = phone.replace(/\D/g, '');
  if (clean.length === 13) return `+${clean.slice(0, 2)} (${clean.slice(2, 4)}) ${clean.slice(4, 5)} ${clean.slice(5, 9)}-${clean.slice(9)}`;
  if (clean.length === 12) return `+${clean.slice(0, 2)} (${clean.slice(2, 4)}) ${clean.slice(4, 8)}-${clean.slice(8)}`;
  if (clean.length === 11) return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7)}`;
  if (clean.length === 10) return `(${clean.slice(0, 2)}) ${clean.slice(2, 6)}-${clean.slice(6)}`;
  return phone;
}

// --- Modo single: escolhe 1 contato (fluxos sem clienteId, ex.: troca/orcamento) ---
function ModalEnviarNFSingle({ tipo, id, numero, aviso, preSelecionarTelefone, onClose }: Props) {
  const [busca, setBusca] = useState('');
  const [contatos, setContatos] = useState<ContatoSugestao[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [contatoSelecionado, setContatoSelecionado] = useState<ContatoSugestao | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [pedirAvaliacao, setPedirAvaliacao] = useState(false);
  const [avalInfo, setAvalInfo] = useState<{ ativo: boolean; elegivel: boolean; ultimo_pedido: string | null } | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Dedup por telefone (mesmo numero pode ter 2 contatos: 1 por inbox atendimento/compras).
  // Prioriza o que tem foto/nome populado.
  function dedupPorTelefone(lista: ContatoSugestao[]): ContatoSugestao[] {
    const porDigits = new Map<string, ContatoSugestao>();
    for (const c of lista) {
      const digits = (c.telefone ?? '').replace(/\D/g, '');
      if (!digits) continue;
      const existente = porDigits.get(digits);
      if (!existente) { porDigits.set(digits, c); continue; }
      // Se o novo tem foto/push_name e o existente nao, troca
      const novoMelhor = (!!c.foto_url && !existente.foto_url) || (!!c.push_name && !existente.push_name);
      if (novoMelhor) porDigits.set(digits, c);
    }
    return [...porDigits.values()];
  }

  // Carga inicial: se cliente tem whatsapp pre-busca por digits; senao traz lista geral
  useEffect(() => {
    let cancelado = false;
    (async () => {
      setBuscando(true);
      try {
        const preDigits = (preSelecionarTelefone ?? '').replace(/\D/g, '');
        // NF e sempre pra cliente — filtra so contatos da inbox atendimento.
        const params = preDigits ? `?busca=${encodeURIComponent(preDigits)}&sem_grupo=true&inbox=atendimento&limit=20` : `?sem_grupo=true&inbox=atendimento&limit=20`;
        const lista = await api.get<ContatoSugestao[]>(`/api/contatos${params}`);
        if (cancelado) return;
        const filtrada = dedupPorTelefone(lista.filter((c) => c.telefone));
        setContatos(filtrada);
        if (preDigits && filtrada.length > 0) {
          setContatoSelecionado(filtrada[0]!);
        } else {
          setTimeout(() => inputRef.current?.focus(), 100);
        }
      } catch {
        // ignora — lista vazia
      } finally {
        if (!cancelado) setBuscando(false);
      }
    })();
    return () => { cancelado = true; };
  }, [preSelecionarTelefone]);

  // Elegibilidade do pedido de avaliacao no Google — depende do contato selecionado.
  useEffect(() => {
    const digits = (contatoSelecionado?.telefone ?? '').replace(/\D/g, '');
    if (!digits) { setAvalInfo(null); setPedirAvaliacao(false); return; }
    let cancelado = false;
    api.get<{ ativo: boolean; elegivel: boolean; ultimo_pedido: string | null }>(
      `/api/avaliacao-google/elegivel?telefone=${digits}`,
    )
      .then((r) => { if (!cancelado) { setAvalInfo(r); setPedirAvaliacao(r.ativo && r.elegivel); } })
      .catch(() => { if (!cancelado) { setAvalInfo(null); setPedirAvaliacao(false); } });
    return () => { cancelado = true; };
  }, [contatoSelecionado]);

  function handleBuscaChange(e: React.ChangeEvent<HTMLInputElement>) {
    const valor = e.target.value;
    setBusca(valor);
    setContatoSelecionado(null);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setBuscando(true);
      try {
        const params = valor.trim().length >= 2
          ? `?busca=${encodeURIComponent(valor.trim())}&sem_grupo=true&inbox=atendimento&limit=30`
          : `?sem_grupo=true&inbox=atendimento&limit=20`;
        const lista = await api.get<ContatoSugestao[]>(`/api/contatos${params}`);
        setContatos(dedupPorTelefone(lista.filter((c) => c.telefone)));
      } catch {
        setContatos([]);
      } finally {
        setBuscando(false);
      }
    }, 400);
  }

  async function handleEnviar() {
    if (!contatoSelecionado?.telefone) return;
    setEnviando(true);
    setErro(null);
    try {
      const digits = contatoSelecionado.telefone.replace(/\D/g, '');
      await api.post(`/api/${tipo}/${id}/enviar-whatsapp`, {
        jid: digits,
        ...(mensagem.trim() ? { mensagem: mensagem.trim() } : {}),
        ...(pedirAvaliacao
          ? { pedir_avaliacao: true, nome_cliente: contatoSelecionado.nome || contatoSelecionado.push_name || '' }
          : {}),
      });
      setEnviado(true);
      setTimeout(() => onClose(true), 1500);
    } catch (err) {
      setErro(motivoLegivel(err));
    } finally {
      setEnviando(false);
    }
  }

  const fileName = `${tipo === 'nfe' ? 'NFe' : 'NFCe'}-${String(numero).padStart(9, '0')}.pdf`;

  // Numero avulso digitado na busca (venda de balcao / cliente sem contato cadastrado).
  // Backend aceita so digitos e completa o DDI 55; aqui so validamos o tamanho.
  const digitosBusca = busca.replace(/\D/g, '');
  const buscaEhNumero = digitosBusca.length >= 10 && digitosBusca.length <= 13;
  const jaExisteContato = contatos.some((c) => {
    const cd = (c.telefone ?? '').replace(/\D/g, '');
    return !!cd && (cd.endsWith(digitosBusca) || digitosBusca.endsWith(cd));
  });
  const mostrarDigitado = buscaEhNumero && !jaExisteContato;
  const contatoDigitado: ContatoSugestao = {
    id: -1, nome: 'Enviar para este numero', push_name: null,
    telefone: digitosBusca, jid: digitosBusca, foto_url: null,
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[80]">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-800">Enviar documento pelo WhatsApp</h3>
          <button onClick={() => onClose(false)} className="p-1 hover:bg-gray-100 rounded-lg transition" aria-label="Fechar">
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        <div className="p-5">
          {aviso && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 mb-3 text-sm text-amber-700">
              <AlertTriangle size={16} className="shrink-0" />
              <span>{aviso}</span>
            </div>
          )}

          {/* Card arquivo */}
          <div className="bg-gray-50 rounded-lg px-3 py-2 mb-3 text-sm text-gray-600 flex items-center gap-2">
            <span className="text-blue-500 font-medium">PDF</span>
            <span className="truncate">{fileName}</span>
          </div>

          {/* Mensagem editavel */}
          <textarea
            value={mensagem}
            onChange={(e) => setMensagem(e.target.value)}
            placeholder="Mensagem (opcional — se vazio, vai a padrao com total e agradecimento)"
            rows={4}
            className="no-upper w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none resize-none mb-3"
          />

          {/* Pedido de avaliacao no Google — so quando ha contato e a feature esta ligada */}
          {avalInfo?.ativo && contatoSelecionado && (
            <label className="flex items-start gap-2 mb-3 text-sm text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={pedirAvaliacao}
                disabled={!avalInfo.elegivel}
                onChange={(e) => setPedirAvaliacao(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Pedir avaliação no Google
                {!avalInfo.elegivel && avalInfo.ultimo_pedido && (
                  <span className="block text-xs text-gray-400">
                    Já solicitada em {new Date(avalInfo.ultimo_pedido).toLocaleDateString('pt-BR')} — aguardando a janela de repetição.
                  </span>
                )}
                {avalInfo.elegivel && (
                  <span className="block text-xs text-gray-400">
                    Envia uma 2ª mensagem curta com o link de avaliação, logo após a nota.
                  </span>
                )}
              </span>
            </label>
          )}

          {/* Busca de contato */}
          <div className="relative mb-3">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={busca}
              onChange={handleBuscaChange}
              placeholder="Buscar contato ou digitar um numero de WhatsApp..."
              className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
            />
            {buscando && <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />}
          </div>

          {/* Lista de contatos */}
          <div className="min-h-[200px] max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
            {mostrarDigitado && (
              <button
                type="button"
                onClick={() => setContatoSelecionado(contatoDigitado)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-green-50 transition border-b border-gray-100 ${contatoSelecionado?.id === -1 ? 'bg-green-50 ring-1 ring-green-400' : ''}`}
              >
                <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                  <Send size={15} className="text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-gray-800">Enviar para este número</div>
                  <div className="text-xs text-gray-400">{formatarTelefoneBr(digitosBusca)}</div>
                </div>
                {contatoSelecionado?.id === -1 && (
                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center shrink-0">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                )}
              </button>
            )}
            {contatos.length === 0 && !buscando && !mostrarDigitado && (
              <div className="text-center text-gray-400 text-sm py-16">
                {busca.length >= 2 ? 'Nenhum contato encontrado — digite o número completo para enviar' : 'Nenhum contato recente'}
              </div>
            )}
            {contatos.map((c) => {
              const nome = c.nome || c.push_name || 'Sem nome';
              const selecionado = contatoSelecionado?.id === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setContatoSelecionado(c)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-green-50 transition border-b border-gray-100 last:border-0 ${selecionado ? 'bg-green-50 ring-1 ring-green-400' : ''}`}
                >
                  {c.foto_url ? (
                    <img src={c.foto_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center">
                      <User size={16} className="text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-800 truncate">{nome}</div>
                    <div className="text-xs text-gray-400">{formatarTelefoneBr(c.telefone)}</div>
                  </div>
                  {selecionado && (
                    <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {erro && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{erro}</div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-200">
          <button onClick={() => onClose(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition">
            Cancelar
          </button>
          <button
            onClick={() => void handleEnviar()}
            disabled={!contatoSelecionado || enviando || enviado}
            className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition ${
              enviado ? 'bg-green-100 text-green-700'
              : contatoSelecionado && !enviando ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {enviado ? <>Enviado!</> : enviando ? <><Loader2 size={16} className="animate-spin" />Enviando...</> : <><Send size={16} />Enviar</>}
          </button>
        </div>
      </div>
    </div>
  );
}
